import io from "socket.io-client";

class Websocket {
  static instance;
  socket;
  userId;
  displayName;
  roomId;
  desiredRoomId;
  onlineUsersCallback;
  heartbeatInterval;
  visibilityListenerAttached;
  connectionStateListener;
  connectionState;

  static CONNECTION_STATUS = {
    CONNECTING: "connecting",
    CONNECTED: "connected",
    RECONNECTING: "reconnecting",
    DISCONNECTED: "disconnected",
    FAILED: "failed",
  };

  constructor() {
    if (!Websocket.instance) {
      Websocket.instance = this;
      this.connectionState = {
        status: Websocket.CONNECTION_STATUS.DISCONNECTED,
        attempt: 0,
        reason: null,
        roomId: null,
      };
    }
    return Websocket.instance;
  }

  initConnection(userId, displayName) {
    this.userId = userId;
    this.displayName = displayName;

    if (this.socket) {
      if (!this.socket.connected) {
        this.notifyConnectionState({
          status: Websocket.CONNECTION_STATUS.CONNECTING,
          reason: "manual_connect",
        });
        this.socket.connect();
      }
      return;
    }

    this.socket = io(process.env.REACT_APP_API_URL, {
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 30000,
      randomizationFactor: 0.5,
      reconnectionAttempts: Number.MAX_SAFE_INTEGER,
      timeout: 20000,
    });

    this.socket.on("connect", () => {
      this.notifyConnectionState({
        status: Websocket.CONNECTION_STATUS.CONNECTED,
        attempt: 0,
        reason: null,
        roomId: this.roomId,
      });
      this.emitUserOnline();
      this.initSidebarRooms();
      this.startHeartbeat();
      this.rejoinDesiredRoom();
    });

    this.socket.on("reconnect", () => {
      this.notifyConnectionState({
        status: Websocket.CONNECTION_STATUS.CONNECTED,
        attempt: 0,
        reason: "reconnected",
        roomId: this.roomId,
      });
      this.emitUserOnline();
      this.initSidebarRooms();
      this.rejoinDesiredRoom();
    });

    this.socket.on("reconnect_attempt", (attempt) => {
      this.notifyConnectionState({
        status: Websocket.CONNECTION_STATUS.RECONNECTING,
        attempt,
        reason: "reconnect_attempt",
        roomId: this.roomId,
      });
      console.log(`Socket reconnect attempt: ${attempt}`);
    });

    this.socket.on("reconnect_failed", () => {
      this.notifyConnectionState({
        status: Websocket.CONNECTION_STATUS.FAILED,
        reason: "reconnect_failed",
        roomId: this.roomId,
      });
      console.error("Socket reconnect attempts are exhausted.");
    });

    this.socket.on("connect_error", (error) => {
      this.notifyConnectionState({
        status: Websocket.CONNECTION_STATUS.RECONNECTING,
        reason: error?.message || "connect_error",
        roomId: this.roomId,
      });
      console.error("Socket connection error:", error?.message || error);
    });

    this.socket.on("disconnect", (reason) => {
      console.log(`${this.displayName || "User"} has disconnected. Reason: ${reason}`);
      this.stopHeartbeat();
      this.roomId = null;
      this.notifyConnectionState({
        status: Websocket.CONNECTION_STATUS.DISCONNECTED,
        reason,
        roomId: null,
      });
    });

    this.socket.on("update_user_list", (userList) => {
      if (typeof this.onlineUsersCallback === "function") {
        this.onlineUsersCallback(userList);
      }
    });

    this.socket.on("ping_pong_response", ({ userId }) => {
      console.log(`Response from server! User ID: ${userId}`);
    });

    this.setupVisibilityChangeListener();
  }

  indicateOnline(userId, displayName, callbackUserList) {
    this.userId = userId;
    this.displayName = displayName;
    this.onlineUsersCallback = callbackUserList;
    this.emitUserOnline();
    this.startHeartbeat();
  }

  logout() {
    const socket = this.socket;
    if (socket && socket.connected) {
      socket.disconnect();
    }
  }

  setupListeners(onMessageReceived) {
    this.socket.on("message", (data) => {
      if (
        (data && (data.type === "user-chat" || data.type === "user-actions-chat")) ||
        data.type === "user-thread-chat" ||
        data.type === "user-reactions-chat"
      ) {
        onMessageReceived(data.message_detail);
      }
    });
    this.socket.on("messageReaction", (data) => {
      if (data && data.type === "user-reactions-chat") {
        onMessageReceived(data.message_detail);
      }
    });
  }

  seenMessage(user_id, display_name, conversation_id, message_id) {
    this.socket.emit("seen_message", {
      userId: user_id,
      display_name: display_name,
      conversationId: conversation_id,
      messageId: message_id,
    });
  }

  receiveSeenMessage(onSeenMessage) {
    this.socket.on("receive_seen_message", (data) => {
      console.log(data);
      onSeenMessage(data);
    });
  }

  pinMessage(conversation_id, user_id, pin_data) {
    this.socket.emit("pin_message", {
      conversation_id,
      user_id,
      pin_data,
    });
  }

  receivePinMessage(onPinMessage) {
    this.socket.on("receive_pin_message", (data) => {
      console.log(data);
      onPinMessage(data);
    });
  }

  setupDisconnectListener() {
    this.socket.on("disconnect", () => {
      console.log("Socket disconnected");
    });
  }

  setConnectionStateListener(listener) {
    this.connectionStateListener = listener;
    if (typeof this.connectionStateListener === "function") {
      this.connectionStateListener(this.connectionState);
    }
  }

  getConnectionState() {
    return this.connectionState;
  }

  getConnection() {
    return this.socket;
  }

  isConnectedRoom() {
    return this.socket && this.roomId ? { connected: true, socket: this.socket } : { connected: false };
  }

  connectRoom(roomId) {
    if (!roomId) return;

    this.desiredRoomId = roomId;

    if (!this.socket) return;

    if (this.socket.connected && this.roomId === roomId) {
      return;
    }

    if (this.roomId && this.roomId !== roomId) {
      this.socket.emit("leave", { userId: this.userId, roomId: this.roomId }, () => {});
    }

    if (!this.socket.connected) {
      this.socket.connect();
      return;
    }

    this.socket.emit(
      "join",
      { userId: this.userId, name: this.displayName, room: roomId },
      (error) => {
        if (!error) {
          this.roomId = roomId;
          this.notifyConnectionState({
            status: this.connectionState.status,
            roomId,
          });
        }
      },
    );
  }

  currentlyTyping(userId, display_name, profile_photo, conversation_id) {
    if (this.socket) {
      this.socket.emit("currently_typing", {
        userId: userId,
        display_name: display_name,
        profile_photo: profile_photo,
        room: conversation_id,
      });
    }
  }

  currentlyTypingListener(onTypingReceived) {
    this.socket.on("currently_typing_feedback", (typing) => {
      onTypingReceived(typing);
    });
  }

  sendMessage(messageDetail) {
    if (this.socket) {
      this.socket.emit("sendMessage", {
        message: messageDetail,
        room: messageDetail.message.conversation_id,
        type: messageDetail.type,
      });
    }
  }

  sendMsgReaction(reactionDetail, conversation_id) {
    if (this.socket) {
      this.socket.emit("sendMsgReaction", {
        message: reactionDetail,
        room: conversation_id,
        type: reactionDetail.type,
      });
    }
  }

  disconnectRoom() {
    if (this.socket && this.roomId) {
      this.socket.emit("leave", { userId: this.userId, roomId: this.roomId }, (error) => {
        if (error) {
          alert(error);
        }
      });
    }

    this.roomId = null;
    this.desiredRoomId = null;
    this.notifyConnectionState({
      status: this.connectionState.status,
      roomId: null,
    });
  }

  setupVisibilityChangeListener() {
    if (this.visibilityListenerAttached) return;

    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "visible") {
        if (this.socket && !this.socket.connected) {
          this.socket.connect();
        }
      }
    });

    this.visibilityListenerAttached = true;
  }

  emitUserOnline() {
    if (!this.socket || !this.socket.connected || !this.userId || !this.displayName) {
      return;
    }

    this.socket.emit("user_online", { userId: this.userId, name: this.displayName }, () => {});
  }

  startHeartbeat() {
    if (this.heartbeatInterval || !this.socket || !this.userId) {
      return;
    }

    this.heartbeatInterval = setInterval(() => {
      if (this.socket && this.socket.connected) {
        this.socket.emit("user_online_ping_pong", { userId: this.userId }, () => {});
      }
    }, 60000);
  }

  stopHeartbeat() {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
  }

  rejoinDesiredRoom() {
    if (!this.socket || !this.socket.connected || !this.desiredRoomId) {
      return;
    }

    this.socket.emit(
      "join",
      {
        userId: this.userId,
        name: this.displayName,
        room: this.desiredRoomId,
      },
      (error) => {
        if (!error) {
          this.roomId = this.desiredRoomId;
          this.notifyConnectionState({
            status: this.connectionState.status,
            roomId: this.roomId,
          });
        }
      },
    );
  }

  notifyConnectionState(partialState = {}) {
    this.connectionState = {
      ...(this.connectionState || {}),
      ...partialState,
    };

    if (typeof this.connectionStateListener === "function") {
      this.connectionStateListener(this.connectionState);
    }
  }

  initSidebarRooms() {
    if (!this.socket || !this.socket.connected || !this.userId || !this.displayName) {
      return;
    }

    this.socket.emit(
      "sidebar_join",
      { userId: this.userId, name: this.displayName },
      (error) => {
        if (error) {
          console.error("sidebar_join error:", error);
        }
      },
    );
  }

  setupConversationListListener(onConversationUpdated) {
    if (!this.socket || typeof onConversationUpdated !== "function") return;

    this.socket.off("conversation_updated");
    this.socket.on("conversation_updated", (data) => {
      if (data && data.type === "conversation_updated") {
        onConversationUpdated(data);
      }
    });
  }
}

export default Websocket;
